import {
  getAllStoresProducts,
  getFeaturedProducts,
  getProductCategories,
  getProductCategory,
} from '@api/product';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { Container, Header, Section } from '@components';
import { ProductCardWithProvider, SmallCategoryCard } from '@components/cards';
import { SearchContainer } from '@components/container';
import { CarouselContainer } from '@components/container/carousel';
import { TProduct, TProductCategory } from '@components/types';
import appConfig from '@core/appconfig';
import { getSafeUrl, sanityIoImageLoader } from '@core/utils';
import { ChevronLeft, ChevronRight, LayoutGrid } from 'lucide-react';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  initialProductCategory?: TProductCategory;
  initialProductSubCategories?: TProductCategory[];
  initialProductList?: TProduct[];
}

export const getServerSideProps: GetServerSideProps<{
  initialProductCategory: TProductCategory;
  initialProductSubCategories: TProductCategory[];
  initialProductList: TProduct[];
}> = async (context) => {
  const { slug } = context.params;

  const [
    initialProductCategory,
    initialProductSubCategories,
    initialProductList,
  ] = await Promise.all([
    getProductCategory(parseInt(slug[0] as string)),
    getProductCategories(parseInt(slug[0] as string)),
    getFeaturedProducts(parseInt(slug[0] as string)),
  ]);

  return {
    props: {
      initialProductCategory: initialProductCategory,
      initialProductSubCategories: initialProductSubCategories,
      initialProductList: initialProductList,
    },
  };
};

const Navbar = dynamic(
  () => import('../../src/components/navbar').then((mod) => mod.Navbar),
  {
    ssr: false,
  }
);

const ProductList: React.FC = ({
  initialProductCategory,
  initialProductSubCategories,
  initialProductList,
}: Props) => {
  const router = useRouter();
  const [productCategory, setProductCategory] = useState<TProductCategory>(
    initialProductCategory
  );
  const [subCategories, setSubCategories] = useState<TProductCategory[]>(
    initialProductSubCategories
  );
  const [parentCategory, setParentCategory] = useState<TProductCategory>(null);
  const [products, setProducts] = useState<TProduct[]>(initialProductList);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoadMore, setIsLoadMore] = useState(false);
  const pageSize = 20;

  const getAllFeaturedProducts = (extend: boolean = true) => {
    let id = (
      router.query.slug === undefined ? router.query.slug : router.query.slug[0]
    ) as string;
    if (id != undefined) {
      const resProducts: Promise<TProduct[]> = getAllStoresProducts(
        null,
        parseInt(id),
        pageNumber,
        pageSize
      );
      resProducts.then((result) => {
        setIsLoadMore(result.length == pageSize);
        if (extend) {
          setProducts([...products, ...result]);
        } else {
          setProducts(result);
        }
      });
    }
  };

  useEffect(() => {
    let id = (
      router.query.slug === undefined ? router.query.slug : router.query.slug[0]
    ) as string;
    if (!router.isReady || id == productCategory?.id.toString()) {
      return;
    }
    const res: Promise<TProductCategory> = getProductCategory(parseInt(id));
    res.then((productCategory) => {
      setProductCategory(productCategory);
    });
    const resSubCategories: Promise<TProductCategory[]> = getProductCategories(
      parseInt(id)
    );
    resSubCategories.then((categories) => setSubCategories(categories));
  }, [router.isReady, router.query.slug]);

  // Fetch the parent category so the user can navigate back out of a subcategory
  // instead of getting stuck — there's otherwise no way back to the main category.
  useEffect(() => {
    if (productCategory?.master_id > 0) {
      getProductCategory(productCategory.master_id).then((parent) => {
        setParentCategory(parent || null);
      });
    } else {
      setParentCategory(null);
    }
  }, [productCategory?.id, productCategory?.master_id]);

  useEffect(() => {
    if (pageNumber == 0) {
      getAllFeaturedProducts(pageNumber != 0);
    } else {
      setPageNumber(0);
    }
  }, [router.isReady, router.query.slug]);

  useEffect(() => {
    getAllFeaturedProducts(pageNumber != 0);
  }, [pageNumber]);

  return (
    <Container>
      <Head>
        <title>{`${productCategory?.name} products - ${appConfig.appName}`}</title>
        <meta
          name="Keywords"
          content={`${productCategory?.name} Products, Local Services,${productCategory?.name},Jhattse`}
        />
        <meta
          name="Description"
          content={`Get ${productCategory?.name} products from nearby local stores on ${appConfig.appName}`}
        />
        <link
          rel="canonical"
          href={`https://jhattse.com/category/${productCategory?.id
            }/${getSafeUrl(productCategory?.name)}`}
        />
        <meta
          property="og:title"
          content={`${productCategory?.name} products on ${appConfig.appName}`}
        />
        <meta
          name="og:description"
          content={`Get ${productCategory?.name} products from nearby local stores on ${appConfig.appName}`}
        />
        <meta name="og:image" content={`${productCategory.image}`} />
        <meta
          property="og:url"
          content={`https://jhattse.com/category/${productCategory?.id
            }/${getSafeUrl(productCategory?.name)}`}
        />
      </Head>
      <Header />
      <div className="font-manrope w-full max-w-[1240px] mx-auto">
        <div className="hidden px-2 pt-6 md:flex items-center font-manrope">
          <Breadcrumb className="flex items-center gap-1 text-sm text-neutral-500 font-medium font-manrope list-none">
            <BreadcrumbItem>
              <BreadcrumbLink href="/" className="hover:text-business-500 transition-colors">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <ChevronRight size={14} className="text-neutral-300 mx-1" />
            <BreadcrumbItem>
              <BreadcrumbLink href="/category" className="hover:text-business-500 transition-colors">Category</BreadcrumbLink>
            </BreadcrumbItem>
            {parentCategory && (
              <>
                <ChevronRight size={14} className="text-neutral-300 mx-1" />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    href={`/category/${parentCategory.id}/${getSafeUrl(parentCategory.name)}`}
                    className="hover:text-business-500 transition-colors"
                  >
                    {parentCategory.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            <ChevronRight size={14} className="text-neutral-300 mx-1" />
            <BreadcrumbItem
              className="text-ellipsis text-left break-words line-clamp-1 select-none text-business-500 font-semibold"
              isCurrentPage
            >
              <BreadcrumbLink href={`/`}>
                {' '}
                {productCategory?.name}
              </BreadcrumbLink>
              <ol className="p-0 list-none"></ol>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>

        {parentCategory && (
          <div className="px-2 pt-4 md:hidden">
            <Link
              href={`/category/${parentCategory.id}/${getSafeUrl(parentCategory.name)}`}
              prefetch={false}
              className="inline-flex items-center gap-1 text-sm font-semibold text-business-500 hover:underline"
            >
              <ChevronLeft size={16} />
              Back to {parentCategory.name}
            </Link>
          </div>
        )}

        <div className="px-2 pt-4 md:pt-6">
          <div
            className="relative overflow-hidden rounded-2xl md:rounded-3xl flex items-end min-h-32 md:min-h-44"
            style={{ background: 'linear-gradient(120deg, #0F6E56 0%, #0B5D57 60%, #093F32 100%)' }}
          >
            {productCategory?.image && (
              <Image
                loader={sanityIoImageLoader}
                src={productCategory.image}
                alt={productCategory?.name || 'Category'}
                fill
                sizes="(max-width: 768px) 100vw, 1240px"
                className="object-cover opacity-25"
              />
            )}
            <div className="relative z-10 flex items-center gap-3 p-4 md:p-6 w-full">
              <div className="flex items-center justify-center w-11 h-11 md:w-14 md:h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex-shrink-0">
                <LayoutGrid className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={1.75} />
              </div>
              <div className="flex flex-col gap-0.5 min-w-0">
                <h1 className="text-lg md:text-2xl text-white font-extrabold line-clamp-1">
                  {productCategory?.name}
                </h1>
                <p className="text-xs md:text-sm text-white/70 font-medium">
                  {products?.length > 0 ? `${products.length}+ products available nearby` : 'Explore products from nearby stores'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-2">
          {subCategories?.length > 0 && (
            <div className="pt-5 pb-1">
              <h2 className="font-manrope text-sm font-bold text-neutral-500 uppercase tracking-wide px-1 pb-2 flex items-center gap-1.5">
                <span className="w-1 h-3.5 rounded-full bg-brand-500 inline-block"></span>
                Browse subcategories
              </h2>
            </div>
          )}
          <div className="border-neutral-400 pb-4 hidden md:block">
            <Section
              products={subCategories}
              element={SmallCategoryCard}
              intent="category"
              style="bg-white text-neutral-900"
              headStyle="p-2 font-medium text-base"
            />
          </div>
          <div className="block md:hidden pb-2">
            <CarouselContainer>
              {subCategories?.map((subCategory) => (
                <SwiperSlide style={{ width: 'auto' }} className="min-w-0">
                  <Link
                    href={`/category/${subCategory?.id}/${getSafeUrl(
                      subCategory?.name
                    )}`}
                    prefetch={false}
                  >
                    <div className="bg-white border-neutral-200/70 border shadow-sm px-3 py-2 font-semibold text-neutral-700 hover:border-business-500/40 hover:text-business-500 transition-colors cursor-pointer rounded-full text-sm font-manrope whitespace-nowrap">
                      {subCategory?.name}
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </CarouselContainer>
          </div>
        </div>

        <div className="px-2 pt-3 pb-20">
          {products?.length > 0 && (
            <h2 className="font-manrope text-sm font-bold text-neutral-500 uppercase tracking-wide px-1 pb-3 flex items-center gap-1.5">
              <span className="w-1 h-3.5 rounded-full bg-brand-500 inline-block"></span>
              All products
            </h2>
          )}
          <SearchContainer
            searchresults={products}
            element={ProductCardWithProvider}
            isLoadMore={isLoadMore}
            pageNumber={pageNumber}
            showStoreName={true}
            setPageNumber={setPageNumber}
          />
        </div>
      </div>
      <Navbar />
    </Container>
  );
};

export default ProductList;
